import { useAuthStore } from '../stores/auth';
import { usePageHeaderStore } from '../stores/pageHeader';
import { User, Shield, Key, Bell, Save, Settings, AlertTriangle, Check, Trash2, Eye, EyeOff, MessageCircle, Mail, Loader2, Sparkles, Server, Globe, Download, HelpCircle, Building2, CreditCard, BarChart3, Package, Heart, Headphones } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';

interface GlobalSettingsResponse {
  claudeApiKey: {
    configured: boolean;
    keyPrefix: string | null;
    updatedAt: string | null;
    updatedBy: string | null;
  };
  isSuperAdmin: boolean;
}

interface AuthProvider {
  id: string;
  type: 'email' | 'telegram' | 'google' | 'github';
  enabled: boolean;
  allowRegistration: boolean;
  displayName: string;
  displayOrder: number;
  settings: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

interface AuthProvidersResponse {
  providers: AuthProvider[];
}

// Generation Settings Types
type DetailLevel = 'brief' | 'standard' | 'detailed';
type PriorityFramework = 'moscow' | 'high-medium-low' | 'rice' | 'kano' | 'value-effort' | 'custom';
type OutputFormat = 'use-cases' | 'given-when-then' | 'simple';

interface GenerationSettings {
  askClarifyingQuestions: boolean;
  priorityFramework: PriorityFramework;
  detailLevel: DetailLevel;
  outputFormat: OutputFormat;
}

// Related Systems Types - Business Systems
type SystemCategory = 'admin' | 'marketing' | 'content' | 'payments' | 'analytics' | 'integration' | 'inventory' | 'support' | 'custom';

interface RelatedSystem {
  id: string;
  name: string;
  type: 'predefined' | 'custom';
  category: SystemCategory;
  enabled: boolean;
  description: string;
  color?: string;
  order: number;
}

interface RelatedSystemsSettings {
  generateCrossSystemUseCases: boolean;
  groupBySystem: boolean;
  includeIntegrationPoints: boolean;
  relatedSystemsDetailLevel: 'minimal' | 'standard' | 'detailed';
  maxUseCasesPerSystem: number;
}

// Category icons and labels for business systems
const categoryIcons: Record<SystemCategory, React.ElementType> = {
  admin: Building2,
  marketing: Heart,
  content: Globe,
  payments: CreditCard,
  analytics: BarChart3,
  integration: Bell,
  inventory: Package,
  support: Headphones,
  custom: Settings,
};

const categoryLabels: Record<SystemCategory, string> = {
  admin: 'Administrative',
  marketing: 'Marketing',
  content: 'Content',
  payments: 'Payments',
  analytics: 'Analytics',
  integration: 'Integration',
  inventory: 'Inventory',
  support: 'Support',
  custom: 'Custom Systems',
};

const categoryOrder: SystemCategory[] = ['admin', 'marketing', 'content', 'payments', 'analytics', 'integration', 'inventory', 'support', 'custom'];

const detailOptions: { id: DetailLevel; label: string }[] = [
  { id: 'brief', label: 'Brief' },
  { id: 'standard', label: 'Standard' },
  { id: 'detailed', label: 'Detailed' },
];

const priorityOptions: { id: PriorityFramework; label: string }[] = [
  { id: 'moscow', label: 'MoSCoW' },
  { id: 'high-medium-low', label: 'High / Medium / Low' },
  { id: 'rice', label: 'RICE Score' },
  { id: 'kano', label: 'Kano Model' },
  { id: 'value-effort', label: 'Value vs Effort' },
  { id: 'custom', label: 'Custom' },
];

const outputFormatOptions: { id: OutputFormat; label: string; description: string }[] = [
  { id: 'use-cases', label: 'Use Cases', description: 'Detailed scenarios with flows' },
  { id: 'given-when-then', label: 'Given-When-Then', description: 'BDD-style criteria' },
  { id: 'simple', label: 'Simple AC', description: 'Short acceptance criteria' },
];

export function SettingsPage() {
  const { user } = useAuthStore();
  const { setHeader, clearHeader } = usePageHeaderStore();
  
  // Global API Key state (for superadmins)
  const [globalSettings, setGlobalSettings] = useState<GlobalSettingsResponse | null>(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [newApiKey, setNewApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Auth Providers state
  const [authProviders, setAuthProviders] = useState<AuthProvider[]>([]);
  const [isLoadingProviders, setIsLoadingProviders] = useState(true);
  const [providerSaving, setProviderSaving] = useState<string | null>(null);
  const [providerMessage, setProviderMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [telegramBotUsername, setTelegramBotUsername] = useState('');
  const [googleClientId, setGoogleClientId] = useState('');
  
  // Generation Settings state
  const [generationSettings, setGenerationSettings] = useState<GenerationSettings>({
    askClarifyingQuestions: true,
    priorityFramework: 'moscow',
    detailLevel: 'standard',
    outputFormat: 'use-cases',
  });
  const [isLoadingGenerationSettings, setIsLoadingGenerationSettings] = useState(true);
  const [, setIsSavingGenerationSettings] = useState(false);
  const [generationSettingsMessage, setGenerationSettingsMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Related Systems state
  const [systems, setSystems] = useState<RelatedSystem[]>([]);
  const [relatedSystemsSettings, setRelatedSystemsSettings] = useState<RelatedSystemsSettings>({
    generateCrossSystemUseCases: true,
    groupBySystem: true,
    includeIntegrationPoints: true,
    relatedSystemsDetailLevel: 'standard',
    maxUseCasesPerSystem: 5,
  });
  const [isLoadingRelatedSystems, setIsLoadingRelatedSystems] = useState(true);
  const [isSavingRelatedSystems, setIsSavingRelatedSystems] = useState(false);
  const [relatedSystemsMessage, setRelatedSystemsMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  useEffect(() => {
    setHeader(
      'Settings',
      'Manage your account and preferences',
      <Settings className="w-5 h-5" style={{ color: 'var(--primary)' }} />
    );
    return () => clearHeader();
  }, [setHeader, clearHeader]);
  
  // Fetch global settings
  useEffect(() => {
    async function fetchGlobalSettings() {
      try {
        const response = await api.get<GlobalSettingsResponse>('/global-settings');
        setGlobalSettings(response);
      } catch (error) {
        console.error('Failed to fetch global settings:', error);
      } finally {
        setIsLoadingSettings(false);
      }
    }
    
    fetchGlobalSettings();
  }, []);
  
  // Fetch related systems
  useEffect(() => {
    async function fetchRelatedSystems() {
      try {
        const response = await api.get<{
          systems: RelatedSystem[];
          settings: RelatedSystemsSettings;
          categoryLabels: Record<string, string>;
        }>('/user-settings/related-systems');
        setSystems(response.systems);
        if (response.settings) {
          setRelatedSystemsSettings(response.settings);
        }
      } catch (error) {
        console.error('Failed to fetch related systems:', error);
      } finally {
        setIsLoadingRelatedSystems(false);
      }
    }
    
    fetchRelatedSystems();
  }, []);

  // Fetch generation settings
  useEffect(() => {
    async function fetchGenerationSettings() {
      try {
        const response = await api.get<GenerationSettings>('/user-settings/generation');
        setGenerationSettings(prev => ({ ...prev, ...response }));
      } catch (error) {
        console.error('Failed to fetch generation settings:', error);
      } finally {
        setIsLoadingGenerationSettings(false);
      }
    }
    
    fetchGenerationSettings();
  }, []);
  
  // Fetch auth providers (superadmin only)
  useEffect(() => {
    if (!user?.isSuperAdmin) {
      setIsLoadingProviders(false);
      return;
    }
    
    async function fetchAuthProviders() {
      try {
        const response = await api.get<AuthProvidersResponse>('/auth-providers');
        setAuthProviders(response.providers);
        
        // Set telegram bot username from settings
        const telegramProvider = response.providers.find(p => p.type === 'telegram');
        if (telegramProvider) {
          setTelegramBotUsername((telegramProvider.settings.botUsername as string) || '');
        }
        
        // Set google client id from settings
        const googleProvider = response.providers.find(p => p.type === 'google');
        if (googleProvider) {
          setGoogleClientId((googleProvider.settings.clientId as string) || '');
        }
      } catch (error) {
        console.error('Failed to fetch auth providers:', error);
      } finally {
        setIsLoadingProviders(false);
      }
    }
    
    fetchAuthProviders();
  }, [user?.isSuperAdmin]);
  
  // Save API key
  const handleSaveApiKey = async () => {
    if (!newApiKey.trim()) {
      setSaveMessage({ type: 'error', text: 'Please enter an API key' });
      return;
    }
    
    setIsSaving(true);
    setSaveMessage(null);
    
    try {
      const response = await api.post<{ success: boolean; keyPrefix: string; message: string }>(
        '/global-settings/claude-key',
        { apiKey: newApiKey }
      );
      
      if (response.success) {
        setSaveMessage({ type: 'success', text: 'API key saved successfully' });
        setNewApiKey('');
        // Refresh settings
        const updated = await api.get<GlobalSettingsResponse>('/global-settings');
        setGlobalSettings(updated);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to save API key';
      setSaveMessage({ type: 'error', text: message });
    } finally {
      setIsSaving(false);
    }
  };
  
  // Delete API key
  const handleDeleteApiKey = async () => {
    if (!confirm('Are you sure you want to delete the global Claude API key? This will affect all users.')) {
      return;
    }
    
    setIsDeleting(true);
    setSaveMessage(null);
    
    try {
      await api.delete('/global-settings/claude-key');
      setSaveMessage({ type: 'success', text: 'API key deleted' });
      // Refresh settings
      const updated = await api.get<GlobalSettingsResponse>('/global-settings');
      setGlobalSettings(updated);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to delete API key';
      setSaveMessage({ type: 'error', text: message });
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Toggle provider enabled
  const handleToggleProvider = async (providerType: string, enabled: boolean) => {
    setProviderSaving(providerType);
    setProviderMessage(null);
    
    try {
      await api.patch(`/auth-providers/${providerType}`, { enabled });
      
      // Refresh providers
      const response = await api.get<AuthProvidersResponse>('/auth-providers');
      setAuthProviders(response.providers);
      
      setProviderMessage({ 
        type: 'success', 
        text: `${providerType.charAt(0).toUpperCase() + providerType.slice(1)} ${enabled ? 'enabled' : 'disabled'}` 
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to update provider';
      setProviderMessage({ type: 'error', text: message });
    } finally {
      setProviderSaving(null);
    }
  };
  
  // Toggle allow registration
  const handleToggleRegistration = async (providerType: string, allowRegistration: boolean) => {
    setProviderSaving(providerType);
    setProviderMessage(null);
    
    try {
      await api.patch(`/auth-providers/${providerType}`, { allowRegistration });
      
      // Refresh providers
      const response = await api.get<AuthProvidersResponse>('/auth-providers');
      setAuthProviders(response.providers);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to update provider';
      setProviderMessage({ type: 'error', text: message });
    } finally {
      setProviderSaving(null);
    }
  };
  
  // Save Telegram bot username
  const handleSaveTelegramSettings = async () => {
    setProviderSaving('telegram');
    setProviderMessage(null);
    
    try {
      await api.patch('/auth-providers/telegram', { 
        settings: { botUsername: telegramBotUsername } 
      });
      
      // Refresh providers
      const response = await api.get<AuthProvidersResponse>('/auth-providers');
      setAuthProviders(response.providers);
      
      setProviderMessage({ type: 'success', text: 'Telegram settings saved' });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to save Telegram settings';
      setProviderMessage({ type: 'error', text: message });
    } finally {
      setProviderSaving(null);
    }
  };
  
  // Save Google client id
  const handleSaveGoogleSettings = async () => {
    setProviderSaving('google');
    setProviderMessage(null);
    
    try {
      await api.patch('/auth-providers/google', { 
        settings: { clientId: googleClientId } 
      });
      
      // Refresh providers
      const response = await api.get<AuthProvidersResponse>('/auth-providers');
      setAuthProviders(response.providers);
      
      setProviderMessage({ type: 'success', text: 'Google settings saved' });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to save Google settings';
      setProviderMessage({ type: 'error', text: message });
    } finally {
      setProviderSaving(null);
    }
  };
  
  const getProviderIcon = (type: string) => {
    switch (type) {
      case 'email':
        return <Mail className="w-4 h-4" />;
      case 'telegram':
        return <MessageCircle className="w-4 h-4" />;
      default:
        return <Key className="w-4 h-4" />;
    }
  };
  
  // Toggle system in related systems
  const toggleSystem = (id: string) => {
    setSystems(prev => prev.map(s => 
      s.id === id ? { ...s, enabled: !s.enabled } : s
    ));
  };
  
  // Save related systems settings
  const saveRelatedSystems = async () => {
    setIsSavingRelatedSystems(true);
    setRelatedSystemsMessage(null);
    
    try {
      await api.put('/user-settings/related-systems', {
        systems: systems.map(s => ({ id: s.id, enabled: s.enabled })),
        settings: relatedSystemsSettings,
      });
      setRelatedSystemsMessage({ type: 'success', text: 'Related systems saved' });
      setTimeout(() => setRelatedSystemsMessage(null), 3000);
    } catch (error) {
      console.error('Failed to save related systems:', error);
      setRelatedSystemsMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setIsSavingRelatedSystems(false);
    }
  };

  // Save generation settings
  const saveGenerationSettings = async (newSettings: GenerationSettings) => {
    setIsSavingGenerationSettings(true);
    setGenerationSettingsMessage(null);
    setGenerationSettings(newSettings);
    
    try {
      await api.put('/user-settings/generation', newSettings);
      setGenerationSettingsMessage({ type: 'success', text: 'Generation settings saved' });
      setTimeout(() => setGenerationSettingsMessage(null), 3000);
    } catch (error) {
      console.error('Failed to save generation settings:', error);
      setGenerationSettingsMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setIsSavingGenerationSettings(false);
    }
  };
  
  // Group systems by category
  const systemsByCategory = systems.reduce((acc, system) => {
    if (!acc[system.category]) {
      acc[system.category] = [];
    }
    acc[system.category].push(system);
    return acc;
  }, {} as Record<SystemCategory, RelatedSystem[]>);
  
  const enabledSystemsCount = systems.filter(s => s.enabled).length;
  
  return (
    <div className="space-y-6 max-w-4xl animate-fade-in">
      
      {/* Superadmin: Auth Providers */}
      {user?.isSuperAdmin && (
        <div className="card" style={{ borderColor: 'var(--info)' }}>
          <div className="card-header">
            <div className="w-8 h-8 rounded-md flex items-center justify-center"
              style={{ background: 'var(--info-dim)' }}
            >
              <Shield className="w-4 h-4" style={{ color: 'var(--info)' }} />
            </div>
            <div>
              <h2 className="card-title">Authentication Providers</h2>
              <p className="text-sm text-muted">Configure login methods for users</p>
            </div>
          </div>
          
          {isLoadingProviders ? (
            <div className="p-4 text-center text-muted flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading providers...
            </div>
          ) : (
            <div className="space-y-4">
              {/* Provider message */}
              {providerMessage && (
                <div 
                  className="p-3 rounded-card text-sm"
                  style={{ 
                    background: providerMessage.type === 'success' ? 'var(--success-dim)' : 'var(--error-dim)',
                    color: providerMessage.type === 'success' ? 'var(--success)' : 'var(--error)'
                  }}
                >
                  {providerMessage.text}
                </div>
              )}
              
              {authProviders.map(provider => (
                <div 
                  key={provider.id}
                  className="p-4 rounded-card"
                  style={{ background: 'var(--surface-2)' }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-8 h-8 rounded-md flex items-center justify-center"
                        style={{ 
                          background: provider.enabled ? 'var(--primary-dim)' : 'var(--surface-3)',
                          color: provider.enabled ? 'var(--primary)' : 'var(--muted)'
                        }}
                      >
                        {getProviderIcon(provider.type)}
                      </div>
                      <div>
                        <p className="font-medium text-default">{provider.displayName}</p>
                        <p className="text-xs text-muted">
                          {provider.type === 'email' && 'Login with email and password'}
                          {provider.type === 'telegram' && 'Login with Telegram account'}
                          {provider.type === 'google' && 'Login with Google account'}
                          {provider.type === 'github' && 'Login with GitHub account'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {/* Allow registration toggle */}
                      <label className="flex items-center gap-2 cursor-pointer">
                        <span className="text-xs text-muted">Registration</span>
                        <div className="relative">
                          <input 
                            type="checkbox" 
                            checked={provider.allowRegistration}
                            onChange={(e) => handleToggleRegistration(provider.type, e.target.checked)}
                            disabled={providerSaving === provider.type || !provider.enabled}
                            className="peer sr-only" 
                          />
                          <div 
                            className="w-9 h-5 rounded-full transition-colors pointer-events-none"
                            style={{ 
                              background: provider.allowRegistration && provider.enabled ? 'var(--primary)' : 'var(--surface-3)',
                              opacity: provider.enabled ? 1 : 0.5
                            }}
                          />
                          <div 
                            className="absolute w-4 h-4 rounded-full bg-white shadow-sm pointer-events-none transition-all duration-200"
                            style={{ 
                              top: '2px',
                              left: provider.allowRegistration ? '18px' : '2px'
                            }}
                          />
                        </div>
                      </label>
                      
                      {/* Enabled toggle */}
                      <label className="flex items-center gap-2 cursor-pointer">
                        <span className="text-xs text-muted">Enabled</span>
                        <div className="relative">
                          <input 
                            type="checkbox" 
                            checked={provider.enabled}
                            onChange={(e) => handleToggleProvider(provider.type, e.target.checked)}
                            disabled={providerSaving === provider.type}
                            className="peer sr-only" 
                          />
                          <div 
                            className="w-9 h-5 rounded-full transition-colors pointer-events-none"
                            style={{ background: provider.enabled ? 'var(--success)' : 'var(--surface-3)' }}
                          />
                          <div 
                            className="absolute w-4 h-4 rounded-full bg-white shadow-sm pointer-events-none transition-all duration-200"
                            style={{ 
                              top: '2px',
                              left: provider.enabled ? '18px' : '2px'
                            }}
                          />
                        </div>
                      </label>
                    </div>
                  </div>
                  
                  {/* Telegram-specific settings */}
                  {provider.type === 'telegram' && provider.enabled && (
                    <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
                      <label className="label text-xs">Bot Username</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={telegramBotUsername}
                          onChange={(e) => setTelegramBotUsername(e.target.value)}
                          className="input flex-1"
                          placeholder="YourBot_bot"
                        />
                        <button
                          onClick={handleSaveTelegramSettings}
                          disabled={providerSaving === 'telegram'}
                          className="btn btn-secondary text-sm"
                        >
                          {providerSaving === 'telegram' ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            'Save'
                          )}
                        </button>
                      </div>
                      <p className="text-xs text-muted mt-1">
                        The username of your Telegram bot (without @). Bot token must be set in environment variables.
                      </p>
                    </div>
                  )}
                  
                  {/* Google-specific settings */}
                  {provider.type === 'google' && provider.enabled && (
                    <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
                      <label className="label text-xs">Google Client ID</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={googleClientId}
                          onChange={(e) => setGoogleClientId(e.target.value)}
                          className="input flex-1"
                          placeholder="xxxx.apps.googleusercontent.com"
                        />
                        <button
                          onClick={handleSaveGoogleSettings}
                          disabled={providerSaving === 'google'}
                          className="btn btn-secondary text-sm"
                        >
                          {providerSaving === 'google' ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            'Save'
                          )}
                        </button>
                      </div>
                      <p className="text-xs text-muted mt-1">
                        Your Google OAuth 2.0 Client ID. Client secret must be set in environment variables.
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Superadmin: Global API Key */}
      {user?.isSuperAdmin && (
        <div className="card" style={{ borderColor: 'var(--warning)' }}>
          <div className="card-header">
            <div className="w-8 h-8 rounded-md flex items-center justify-center"
              style={{ background: 'var(--warning-dim)' }}
            >
              <AlertTriangle className="w-4 h-4" style={{ color: 'var(--warning)' }} />
            </div>
            <div>
              <h2 className="card-title">Global API Key</h2>
              <p className="text-sm text-muted">Superadmin only - this key is used by all organizations</p>
            </div>
          </div>
          
          {isLoadingSettings ? (
            <div className="p-4 text-center text-muted">Loading settings...</div>
          ) : (
            <div className="space-y-4">
              {/* Current key status */}
              <div 
                className="p-4 rounded-card"
                style={{ background: 'var(--surface-2)' }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-default">Claude API Key</p>
                    {globalSettings?.claudeApiKey.configured ? (
                      <div className="text-sm text-muted">
                        <span className="font-mono">{globalSettings.claudeApiKey.keyPrefix}</span>
                        <span className="mx-2">•</span>
                        Updated {globalSettings.claudeApiKey.updatedAt ? new Date(globalSettings.claudeApiKey.updatedAt).toLocaleDateString() : 'unknown'}
                        {globalSettings.claudeApiKey.updatedBy && (
                          <span> by {globalSettings.claudeApiKey.updatedBy}</span>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm" style={{ color: 'var(--error)' }}>Not configured</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {globalSettings?.claudeApiKey.configured ? (
                      <span 
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                        style={{ background: 'var(--success-dim)', color: 'var(--success)' }}
                      >
                        <Check className="w-3 h-3" />
                        Active
                      </span>
                    ) : (
                      <span 
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                        style={{ background: 'var(--error-dim)', color: 'var(--error)' }}
                      >
                        <AlertTriangle className="w-3 h-3" />
                        Missing
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Input for new key */}
              <div>
                <label className="label">
                  {globalSettings?.claudeApiKey.configured ? 'Update API Key' : 'Set API Key'}
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type={showApiKey ? 'text' : 'password'}
                      value={newApiKey}
                      onChange={(e) => setNewApiKey(e.target.value)}
                      className="input pr-10"
                      placeholder="sk-ant-api03-..."
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-default"
                    >
                      {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <button
                    onClick={handleSaveApiKey}
                    disabled={isSaving || !newApiKey.trim()}
                    className="btn btn-primary"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
                <p className="text-xs text-muted mt-2">
                  This API key will be encrypted and used by all users for AI features
                </p>
              </div>
              
              {/* Delete button */}
              {globalSettings?.claudeApiKey.configured && (
                <div className="flex justify-end">
                  <button
                    onClick={handleDeleteApiKey}
                    disabled={isDeleting}
                    className="btn btn-ghost text-sm"
                    style={{ color: 'var(--error)' }}
                  >
                    <Trash2 className="w-4 h-4" />
                    {isDeleting ? 'Deleting...' : 'Delete Key'}
                  </button>
                </div>
              )}
              
              {/* Save message */}
              {saveMessage && (
                <div 
                  className="p-3 rounded-card text-sm"
                  style={{ 
                    background: saveMessage.type === 'success' ? 'var(--success-dim)' : 'var(--error-dim)',
                    color: saveMessage.type === 'success' ? 'var(--success)' : 'var(--error)'
                  }}
                >
                  {saveMessage.text}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Generation Settings */}
      <div className="card">
        <div className="card-header">
          <div className="w-8 h-8 rounded-md flex items-center justify-center"
            style={{ background: 'var(--primary-dim)' }}
          >
            <Sparkles className="w-4 h-4" style={{ color: 'var(--primary)' }} />
          </div>
          <div>
            <h2 className="card-title">Generation Settings</h2>
            <p className="text-sm text-muted">Configure how requirements are generated</p>
          </div>
        </div>
        
        {isLoadingGenerationSettings ? (
          <div className="p-4 text-center text-muted flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading settings...
          </div>
        ) : (
          <div className="space-y-6">
            {/* Message */}
            {generationSettingsMessage && (
              <div 
                className="p-3 rounded-card text-sm"
                style={{ 
                  background: generationSettingsMessage.type === 'success' ? 'var(--success-dim)' : 'var(--error-dim)',
                  color: generationSettingsMessage.type === 'success' ? 'var(--success)' : 'var(--error)'
                }}
              >
                {generationSettingsMessage.text}
              </div>
            )}

            {/* Ask Clarifying Questions */}
            <label 
              className="flex items-center justify-between p-4 rounded-card cursor-pointer transition-colors"
              style={{ background: 'var(--surface-2)' }}
            >
              <div>
                <p className="font-medium text-default">Ask clarifying questions</p>
                <p className="text-sm text-muted">AI asks questions before generating requirements</p>
              </div>
              <div className="relative">
                <input 
                  type="checkbox" 
                  checked={generationSettings.askClarifyingQuestions}
                  onChange={(e) => saveGenerationSettings({ ...generationSettings, askClarifyingQuestions: e.target.checked })}
                  className="peer sr-only" 
                />
                <div 
                  className="w-11 h-6 rounded-full transition-colors pointer-events-none"
                  style={{ background: generationSettings.askClarifyingQuestions ? 'var(--primary)' : 'var(--surface-3)' }}
                />
                <div 
                  className="absolute w-4 h-4 rounded-full bg-white shadow-sm pointer-events-none transition-all duration-200"
                  style={{ 
                    top: '4px',
                    left: generationSettings.askClarifyingQuestions ? '24px' : '4px'
                  }}
                />
              </div>
            </label>
            
            {/* Output Format */}
            <div>
              <label className="label">Output format</label>
              <div className="space-y-2 max-w-md">
                {outputFormatOptions.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => saveGenerationSettings({ ...generationSettings, outputFormat: opt.id })}
                    className="w-full flex items-center justify-between p-3 text-left rounded-lg border transition-colors"
                    style={{ 
                      borderColor: generationSettings.outputFormat === opt.id ? 'var(--primary)' : 'var(--border)',
                      background: generationSettings.outputFormat === opt.id ? 'var(--primary-dim)' : 'transparent'
                    }}
                  >
                    <div>
                      <p 
                        className="text-sm font-medium"
                        style={{ color: generationSettings.outputFormat === opt.id ? 'var(--primary)' : 'var(--default)' }}
                      >
                        {opt.label}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--muted)' }}>{opt.description}</p>
                    </div>
                    {generationSettings.outputFormat === opt.id && (
                      <Check className="w-4 h-4" style={{ color: 'var(--primary)' }} />
                    )}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Priority Framework */}
            <div>
              <label className="label">Priority framework</label>
              <select
                value={generationSettings.priorityFramework}
                onChange={(e) => saveGenerationSettings({ ...generationSettings, priorityFramework: e.target.value as PriorityFramework })}
                className="input max-w-md"
              >
                {priorityOptions.map(opt => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </select>
            </div>
            
            {/* Default Detail Level */}
            <div>
              <label className="label">Default detail level</label>
              <div className="flex gap-2 max-w-md">
                {detailOptions.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => saveGenerationSettings({ ...generationSettings, detailLevel: opt.id })}
                    className="flex-1 py-2 text-sm rounded-lg border transition-colors"
                    style={{ 
                      borderColor: generationSettings.detailLevel === opt.id ? 'var(--primary)' : 'var(--border)',
                      background: generationSettings.detailLevel === opt.id ? 'var(--primary-dim)' : 'transparent',
                      color: generationSettings.detailLevel === opt.id ? 'var(--primary)' : 'var(--muted)',
                      fontWeight: generationSettings.detailLevel === opt.id ? 500 : 400
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Related Systems */}
      <div className="card">
        <div className="card-header">
          <div className="w-8 h-8 rounded-md flex items-center justify-center"
            style={{ background: 'var(--info-dim)' }}
          >
            <Server className="w-4 h-4" style={{ color: 'var(--info)' }} />
          </div>
          <div>
            <h2 className="card-title">Related Systems</h2>
            <p className="text-sm text-muted">Configure interconnected business systems for cross-system requirements generation</p>
          </div>
        </div>
        
        {isLoadingRelatedSystems ? (
          <div className="p-4 text-center text-muted flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading systems...
          </div>
        ) : (
          <>
            {/* Message */}
            {relatedSystemsMessage && (
              <div 
                className="p-3 rounded-card text-sm mb-4"
                style={{ 
                  background: relatedSystemsMessage.type === 'success' ? 'var(--success-dim)' : 'var(--error-dim)',
                  color: relatedSystemsMessage.type === 'success' ? 'var(--success)' : 'var(--error)'
                }}
              >
                {relatedSystemsMessage.text}
              </div>
            )}
            
            {/* Systems grouped by category */}
            <div 
              className="max-h-80 overflow-y-auto rounded-card border p-3 space-y-4"
              style={{ borderColor: 'var(--border)', background: 'var(--surface-2)' }}
            >
              {categoryOrder.map((category) => {
                const categorySystems = systemsByCategory[category];
                if (!categorySystems || categorySystems.length === 0) return null;
                
                const CategoryIcon = categoryIcons[category];
                
                return (
                  <div key={category}>
                    <div className="flex items-center gap-2 mb-2">
                      <CategoryIcon className="w-4 h-4 text-muted" />
                      <span className="text-xs font-medium text-muted uppercase tracking-wide">
                        {categoryLabels[category]}
                      </span>
                    </div>
                    <div className="space-y-1 ml-6">
                      {categorySystems.map((system) => (
                        <label
                          key={system.id}
                          className="flex items-center gap-3 p-2 rounded-md hover:bg-neutral-50 dark:hover:bg-neutral-700 cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={system.enabled}
                            onChange={() => toggleSystem(system.id)}
                            className="w-4 h-4 rounded"
                            style={{ accentColor: system.color || 'var(--primary)' }}
                          />
                          <div 
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: system.color || 'var(--muted)' }}
                          />
                          <div className="flex-1">
                            <span className="text-sm text-default">{system.name}</span>
                            {system.description && (
                              <p className="text-xs text-muted line-clamp-1">{system.description}</p>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Settings */}
            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-default">Max use cases per system</p>
                  <p className="text-xs text-muted">Limit generated use cases for each related system</p>
                </div>
                <select
                  value={relatedSystemsSettings.maxUseCasesPerSystem}
                  onChange={(e) => setRelatedSystemsSettings(prev => ({ ...prev, maxUseCasesPerSystem: parseInt(e.target.value) }))}
                  className="input w-20"
                >
                  {[1, 2, 3, 5, 10].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Footer */}
            <div className="mt-4 flex items-center justify-between">
              {enabledSystemsCount > 0 && (
                <p className="text-xs text-muted">
                  {enabledSystemsCount} system{enabledSystemsCount !== 1 ? 's' : ''} selected
                </p>
              )}
              <button
                onClick={saveRelatedSystems}
                disabled={isSavingRelatedSystems}
                className="btn btn-primary ml-auto"
              >
                {isSavingRelatedSystems ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Systems
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
      
      {/* Profile */}
      <div className="card">
        <div className="card-header">
          <div className="w-8 h-8 rounded-md flex items-center justify-center"
            style={{ background: 'var(--primary-dim)' }}
          >
            <User className="w-4 h-4" style={{ color: 'var(--primary)' }} />
          </div>
          <h2 className="card-title">Profile</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="label">Email</label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="input"
            />
          </div>
          <div>
            <label className="label">Full Name</label>
            <input
              type="text"
              defaultValue={user?.fullName || ''}
              className="input"
              placeholder="Your name"
            />
          </div>
        </div>
        
        <div className="mt-6 flex justify-end">
          <button className="btn btn-primary">
            <Save className="w-4 h-4" />
            Save Changes
          </button>
        </div>
      </div>
      
      {/* Security */}
      <div className="card">
        <div className="card-header">
          <div className="w-8 h-8 rounded-md flex items-center justify-center"
            style={{ background: 'var(--success-dim)' }}
          >
            <Shield className="w-4 h-4" style={{ color: 'var(--success)' }} />
          </div>
          <h2 className="card-title">Security</h2>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="label">Current Password</label>
            <input
              type="password"
              className="input max-w-md"
              placeholder="••••••••"
            />
          </div>
          <div>
            <label className="label">New Password</label>
            <input
              type="password"
              className="input max-w-md"
              placeholder="••••••••"
            />
          </div>
          <div>
            <label className="label">Confirm New Password</label>
            <input
              type="password"
              className="input max-w-md"
              placeholder="••••••••"
            />
          </div>
        </div>
        
        <div className="mt-6 flex justify-end">
          <button className="btn btn-secondary">
            <Key className="w-4 h-4" />
            Change Password
          </button>
        </div>
      </div>
      
      {/* Notifications */}
      <div className="card">
        <div className="card-header">
          <div className="w-8 h-8 rounded-md flex items-center justify-center"
            style={{ background: 'var(--warning-dim)' }}
          >
            <Bell className="w-4 h-4" style={{ color: 'var(--warning)' }} />
          </div>
          <h2 className="card-title">Notifications</h2>
        </div>
        
        <div className="space-y-3">
          <label 
            className="flex items-center justify-between p-4 rounded-card cursor-pointer transition-colors"
            style={{ background: 'var(--surface-2)' }}
          >
            <div>
              <p className="font-medium text-default">Email notifications</p>
              <p className="text-sm text-muted">Receive updates about your requests</p>
            </div>
            <div className="relative">
              <input type="checkbox" defaultChecked className="peer sr-only" />
              <div className="w-11 h-6 rounded-full peer-checked:bg-primary-600 bg-surface-400 transition-colors"
                style={{ background: 'var(--surface-3)' }}
              />
              <div className="absolute left-1 top-1 w-4 h-4 rounded-full bg-white peer-checked:translate-x-5 transition-transform shadow-sm" />
            </div>
          </label>
          
          <label 
            className="flex items-center justify-between p-4 rounded-card cursor-pointer transition-colors"
            style={{ background: 'var(--surface-2)' }}
          >
            <div>
              <p className="font-medium text-default">Comment notifications</p>
              <p className="text-sm text-muted">Get notified when someone comments on your requests</p>
            </div>
            <div className="relative">
              <input type="checkbox" defaultChecked className="peer sr-only" />
              <div className="w-11 h-6 rounded-full peer-checked:bg-primary-600 bg-surface-400 transition-colors"
                style={{ background: 'var(--surface-3)' }}
              />
              <div className="absolute left-1 top-1 w-4 h-4 rounded-full bg-white peer-checked:translate-x-5 transition-transform shadow-sm" />
            </div>
          </label>
          
          <label 
            className="flex items-center justify-between p-4 rounded-card cursor-pointer transition-colors"
            style={{ background: 'var(--surface-2)' }}
          >
            <div>
              <p className="font-medium text-default">Status change alerts</p>
              <p className="text-sm text-muted">Be notified when request status changes</p>
            </div>
            <div className="relative">
              <input type="checkbox" defaultChecked className="peer sr-only" />
              <div className="w-11 h-6 rounded-full peer-checked:bg-primary-600 bg-surface-400 transition-colors"
                style={{ background: 'var(--surface-3)' }}
              />
              <div className="absolute left-1 top-1 w-4 h-4 rounded-full bg-white peer-checked:translate-x-5 transition-transform shadow-sm" />
            </div>
          </label>
        </div>
      </div>
      
      {/* API Keys Info */}
      <div className="card">
        <div className="card-header">
          <div className="w-8 h-8 rounded-md flex items-center justify-center"
            style={{ background: 'var(--info-dim)' }}
          >
            <Key className="w-4 h-4" style={{ color: 'var(--info)' }} />
          </div>
          <h2 className="card-title">Personal API Keys</h2>
        </div>
        
        <div 
          className="p-4 rounded-card text-sm"
          style={{ background: 'var(--surface-2)' }}
        >
          <p className="text-muted">
            Personal API keys are not yet available. Use project tokens for extension integration.
          </p>
        </div>
      </div>
      
      {/* Data */}
      <div className="card">
        <div className="card-header">
          <div className="w-8 h-8 rounded-md flex items-center justify-center"
            style={{ background: 'var(--warning-dim)' }}
          >
            <Download className="w-4 h-4" style={{ color: 'var(--warning)' }} />
          </div>
          <h2 className="card-title">Data</h2>
        </div>
        
        <div className="space-y-3">
          <button 
            className="w-full btn btn-secondary text-left justify-start"
            onClick={() => {
              // Export all data
              console.log('Export all data');
            }}
          >
            <Download className="w-4 h-4" />
            Export All Data
          </button>
          <button 
            className="w-full btn text-left justify-start"
            style={{ 
              borderColor: 'var(--error)', 
              color: 'var(--error)',
              background: 'transparent'
            }}
            onClick={() => {
              if (confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
                // Delete account
                console.log('Delete account');
              }
            }}
          >
            <Trash2 className="w-4 h-4" />
            Delete Account
          </button>
        </div>
      </div>
      
      {/* Help */}
      <div className="card">
        <div className="card-header">
          <div className="w-8 h-8 rounded-md flex items-center justify-center"
            style={{ background: 'var(--info-dim)' }}
          >
            <HelpCircle className="w-4 h-4" style={{ color: 'var(--info)' }} />
          </div>
          <h2 className="card-title">Help & Support</h2>
        </div>
        
        <div className="space-y-3">
          <a 
            href="mailto:support@specuno.com"
            className="block p-4 rounded-card text-sm hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
            style={{ background: 'var(--surface-2)' }}
          >
            <p className="font-medium text-default">Contact Support</p>
            <p className="text-muted mt-1">support@specuno.com</p>
          </a>
          <a 
            href="https://docs.specuno.com"
            target="_blank"
            rel="noopener noreferrer"
            className="block p-4 rounded-card text-sm hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
            style={{ background: 'var(--surface-2)' }}
          >
            <p className="font-medium text-default">Documentation</p>
            <p className="text-muted mt-1">Learn how to use Specuno</p>
          </a>
        </div>
      </div>
    </div>
  );
}
